import PropTypes from 'prop-types';
import React, { useState } from 'react';

import { GoButton, GoToPageGroup, GoToPageInput, GoToPageLabel } from './styles';

const GO_TO_PAGE_LABEL = 'Go to Page';
const GO_BUTTON_LABEL = 'Go';

/**
 * "Go to Page" zone for the redesigned (GN-37779) pagination footer. Mirrors
 * the validation flow of `ravenjs/lib/PaginationGoTo` (numeric-only, clamped to
 * `1..lastPage`, submit on click or Enter) but styled per the attached Figma
 * spec (normal-case label/button, 56x32 input, #9CDEFF "Go" button).
 *
 * @param {Object}   props
 * @param {number}   props.lastPage The final page number (upper bound)
 * @param {Function} props.onSubmit Called with (event, page) on a valid submit
 * @return {JSX.Element}
 */
function PaginationBarGoTo({ lastPage, onSubmit }) {
    const [inputValue, setInputValue] = useState('');

    const isValid = value => {
        const page = Number.parseInt(value, 10);

        return Boolean(value) && page >= 1 && page <= lastPage;
    };

    const submit = event => {
        if (!isValid(inputValue)) {
            return;
        }
        onSubmit(event, Number.parseInt(inputValue, 10));
        setInputValue('');
    };

    const handleChange = event => {
        const { value } = event.target;

        if (
            value === '' ||
            (Number.parseInt(value, 10) >= 1 && Number.parseInt(value, 10) <= lastPage)
        ) {
            setInputValue(value);
        }
    };

    const handleKeyPress = event => {
        const charStr = String.fromCharCode(
            typeof event.which === 'undefined' ? event.keyCode : event.which
        );

        if (event.charCode === 13) {
            submit(event);

            return;
        }
        if (!charStr.match(/^[0-9]+$/)) {
            event.preventDefault();
        }
    };

    return (
        <GoToPageGroup>
            <GoToPageLabel>{GO_TO_PAGE_LABEL}</GoToPageLabel>
            <GoToPageInput
                type="number"
                min="1"
                max={lastPage}
                value={inputValue}
                onChange={handleChange}
                onKeyPress={handleKeyPress}
                aria-label={GO_TO_PAGE_LABEL}
                data-testid="goToPageInput"
            />
            <GoButton type="button" onClick={submit} disabled={!isValid(inputValue)}>
                {GO_BUTTON_LABEL}
            </GoButton>
        </GoToPageGroup>
    );
}

PaginationBarGoTo.propTypes = {
    lastPage: PropTypes.number,
    onSubmit: PropTypes.func,
};

PaginationBarGoTo.defaultProps = {
    lastPage: 1,
    onSubmit: () => {},
};

export default PaginationBarGoTo;
